import { useTranslations } from 'next-intl';
import { create } from 'zustand';

const ENDPOINT = '/api/protected';

type AuthStatus = 'authorized' | 'unauthorized' | 'authorizing' | 'error';
type LoginStatus = 'loggingIn' | 'loggingOut' | 'loggedOut' | 'loggedIn' | 'none' | 'error';

interface ProtectedEntryState {
  init: boolean;
  id: number | null;
  status: AuthStatus;
  loginStatus: LoginStatus;
  loginError: Error | null;
  loginMessage: string | null;
  authError: Error | null;
  authMessage: string | null;
}

interface ProtectedEntryActions {
  setInit: (init: boolean) => void;
  setLoginStatus: (loginStatus: LoginStatus) => void;
  setLoginError: (loginError: Error | null) => void;
  setLoginMessage: (loginMessage: string | null) => void;
  setAuthError: (authError: Error | null) => void;
  setAuthMessage: (authMessage: string | null) => void;
  setStatus: (status: AuthStatus) => void;
}

interface ProtectedEntryStore extends ProtectedEntryState, ProtectedEntryActions {}

const useProtectedEntryStore = create<ProtectedEntryStore>((set) => ({
  init: false,
  id: null,
  status: 'unauthorized',
  loginStatus: 'none',
  loginError: null,
  loginMessage: null,
  authError: null,
  authMessage: null,
  setInit: (init) => set({ init }),
  setLoginStatus: (loginStatus) => set({ loginStatus }),
  setLoginError: (loginError) => set({ loginError }),
  setLoginMessage: (loginMessage) => set({ loginMessage }),
  setAuthError: (authError) => set({ authError }),
  setAuthMessage: (authMessage) => set({ authMessage }),
  setStatus: (status) => set({ status }),
}));

const useProtectedEntry = (entryId: string) => {
  const t = useTranslations('protected');

  const {
    status,
    loginStatus,
    loginError,
    loginMessage,
    authError,
    authMessage,
    setLoginError,
    setLoginStatus,
    setLoginMessage,
    setAuthError,
    setAuthMessage,
    setStatus,
  } = useProtectedEntryStore();

  const isAuthorized = status === 'authorized';
  const isAuthorizing = status === 'authorizing';
  const isAuthError = !!authError;
  const isLoginError = !!loginError;
  const isLoggingIn = loginStatus === 'loggingIn';
  const isLoggingOut = loginStatus === 'loggingOut';
  const isLoggedOut = loginStatus === 'loggedOut';
  const isLoginSuccess = loginStatus === 'loggedIn';

  const handleLogout = () => {
    setStatus('unauthorized');
    setAuthError(null);
    setAuthMessage(null);
    setLoginError(null);
    setLoginMessage(null);
    setLoginStatus('loggedOut');
  };

  const handleLogin = (password: string) => {
    setLoginStatus('loggingIn');
    setLoginError(null);
    fetch(`${ENDPOINT}/login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'credentials': 'include',
      },
      body: JSON.stringify({
        password,
        id: entryId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const isError = !data.success;

        if (isError) {
          setLoginStatus('error');
          setLoginError(new Error(t('invalidPassword')));
          setStatus('error');
        } else {
          setLoginStatus('loggedIn');

          setStatus(isError ? 'error' : 'authorized');
          setAuthError(data.error);
          setAuthMessage(data.message);
        }
        setLoginMessage(data.message);
      })
      .catch((err) => {
        setLoginStatus('error');
        setLoginError(err);

        setAuthError(err);
        setStatus('error');
      });
  };

  return {
    isAuthorized,
    isAuthorizing,
    isAuthError,
    isLoginError,
    isLoggingIn,
    isLoggingOut,
    isLoggedOut,
    isLoginSuccess,
    loginMessage,
    authMessage,
    handleLogin,
    handleLogout,
  };
};

export default useProtectedEntry;
